import React, { useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import {
  SignatureIcon,
  InitialIcon,
  TextBoxIcon,
  DateIcon,
  CheckBoxIcon,
} from "../tabs/icons";

export const createSvgIcon = (iconType) => {
  const tempDiv = document.createElement("div");

  switch (iconType) {
    case "Signature":
      tempDiv.innerHTML = ReactDOMServer.renderToString(
        <SignatureIcon width={16} height={16} stroke="#2868d7" />
      );
      break;
    case "Initial":
      tempDiv.innerHTML = ReactDOMServer.renderToString(
        <InitialIcon width={16} height={16} stroke="#2868d7" />
      );
      break;
    case "TextBox":
      tempDiv.innerHTML = ReactDOMServer.renderToString(
        <TextBoxIcon width={16} height={16} stroke="#2868d7" />
      );
      break;
    case "Date":
      tempDiv.innerHTML = ReactDOMServer.renderToString(
        <DateIcon width={16} height={16} stroke="#2868d7" />
      );
      break;
    case "Checkbox":
      tempDiv.innerHTML = ReactDOMServer.renderToString(
        <CheckBoxIcon width={16} height={16} stroke="#2868d7" />
      );
      break;
    default:
      return "";
  }

  return tempDiv.innerHTML;
};

const FieldManager = ({ droppedFields, setDroppedFields, activePage }) => {
  // Function to handle element selection
  const handleElementSelect = (fieldId) => {
    // Check if the element is already selected to prevent toggling on the same click
    const alreadySelected = droppedFields.find(
      (item) => item.id === fieldId
    )?.isSelected;

    if (alreadySelected) {
      // If already selected, don't toggle off on the same click
      return;
    }

    setDroppedFields((prev) => {
      return prev.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            isSelected: true,
            borderColor: "#2868d7",
          };
        }
        return {
          ...item,
          isSelected: false,
          borderColor:
            item.borderColor === "#2868d7" ? "#ff9999" : item.borderColor,
        };
      });
    });
  };

  // Function to show text field editor
  const showTextFieldEditor = (field, element) => {
    const input = document.createElement("input");
    input.type = field.tabType === "Date" ? "date" : "text";
    input.value = field.value || "";
    input.style.position = "absolute";
    input.style.left = element.style.left;
    input.style.bottom = element.style.bottom;
    input.style.width = element.style.width;
    input.style.height = element.style.height;
    input.style.zIndex = "1000";
    input.style.padding = "5px";
    input.style.border = "2px solid #2868d7";
    input.style.borderRadius = "4px";
    input.style.backgroundColor = "#ffffff";

    input.addEventListener("blur", () => {
      setDroppedFields((prev) => {
        const updatedFields = prev.map((item) => {
          if (item.id === field.id) {
            return { ...item, value: input.value };
          }
          return item;
        });

        const updatedField = updatedFields.find((item) => item.id === field.id);
        if (updatedField) {
          const existingElement = document.getElementById(field.id);
          if (existingElement && updatedField.tabType === "TextBox") {
            existingElement.innerHTML = `${
              !updatedField.value
                ? `<div style="font-size: 10px; line-height: 12px; text-align: center;">TextBox</div>`
                : ""
            }
            ${
              updatedField.value
                ? `<div style="font-size: 12px; line-height: 14px; text-align: center; max-height: 60px; overflow: hidden; text-overflow: ellipsis;">${updatedField.value}</div>`
                : ""
            }`;

            // Re-attach event listeners
            existingElement.addEventListener("dblclick", (e) => {
              e.stopPropagation();
              showTextFieldEditor(updatedField, existingElement);
            });

            existingElement.addEventListener("click", (e) => {
              e.stopPropagation();
              // Check if already selected to prevent toggling
              const alreadySelected = droppedFields.find(
                (item) => item.id === updatedField.id
              )?.isSelected;
              if (!alreadySelected) {
                handleElementSelect(updatedField.id);
              }
            });

            existingElement.setAttribute("draggable", "true");
            existingElement.addEventListener("dragstart", (e) => {
              e.dataTransfer?.setData("text/plain", updatedField.id);
            });
          }
        }

        return updatedFields;
      });

      if (input.parentNode) {
        input.parentNode.removeChild(input);
      }
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        input.blur();
      }
    });

    const fieldWrapper = document.getElementById(`fieldWrap${activePage}`);
    if (fieldWrapper) {
      fieldWrapper.appendChild(input);
      input.focus();
    }
  };

  // Function to add a dropped field to the canvas
  const addDroppedField = (field, pageIndex) => {
    const fieldWrapper = document.getElementById(`fieldWrap${pageIndex}`);
    if (!fieldWrapper) return;

    let element;

    const applyCommonStyling = (el) => {
      el.id = field.id;
      el.style.position = "absolute";
      el.style.left = `${field.xPos}%`;
      el.style.bottom = `${field.yPos}%`;
      el.style.width = `${field.width}px`;
      el.style.height = `${field.height}px`;
      el.style.backgroundColor = field.color || "#e6eaff";
      el.style.color = field.textColor || "#2868d7";
      el.style.border = `${field.borderWidth || 1}px ${
        field.borderStyle || "solid"
      } ${field.borderColor || "#ff9999"}`;
      el.style.borderRadius = "2px";
      el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
      el.style.padding = "5px";
      el.style.boxSizing = "border-box";
      el.style.cursor = "pointer";

      el.addEventListener("click", (e) => {
        e.stopPropagation();
        // Check if already selected to prevent toggling
        const alreadySelected = droppedFields.find(
          (item) => item.id === field.id
        )?.isSelected;
        if (!alreadySelected) {
          handleElementSelect(field.id);
        }
      });

      el.setAttribute("draggable", "true");
      el.addEventListener("dragstart", (e) => {
        e.dataTransfer?.setData("text/plain", field.id);
      });
    };

    switch (field.tabType) {
      case "Signature":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.flexDirection = "column";
        element.style.alignItems = "center";
        element.style.justifyContent = "center";
        element.style.gap = "2px";
        element.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 14px; width: 20px;">${createSvgIcon(
          "Signature"
        )}</div><div id={${
          field.id + "signature"
        }}  style="font-size: 10px; line-height: 12px; text-align: center;">${
          field.user.first_name
        } ${field.user.last_name}</div>`;
        applyCommonStyling(element);
        break;

      case "Initial":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.flexDirection = "column";
        element.style.alignItems = "center";
        element.style.justifyContent = "center";
        element.style.gap = "2px";
        element.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 16px; width: 20px;">${createSvgIcon(
          "Initial"
        )}</div><div id={${
          field.id
        }} style="font-size: 10px; line-height: 12px; text-align: center;">${
          field.user.first_name[0]
        }${field.user.last_name[0]}</div>`;
        applyCommonStyling(element);
        break;

      case "TextBox":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.flexDirection = "column";
        element.style.alignItems = "center";
        element.style.justifyContent = "center";
        element.style.gap = "2px";
        element.innerHTML = `${
          !field.value
            ? `<div style="font-size: 10px; line-height: 12px; text-align: center;">TextBox</div>`
            : ""
        }
        ${
          field.value
            ? `<div style="font-size: 12px; line-height: 14px; text-align: center; max-height: 60px; overflow: hidden; text-overflow: ellipsis;">${field.value}</div>`
            : ""
        }`;
        applyCommonStyling(element);

        element.addEventListener("dblclick", (e) => {
          e.stopPropagation();
          showTextFieldEditor(field, element);
        });
        break;

      case "Date":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.flexDirection = "column";
        element.style.alignItems = "center";
        element.style.justifyContent = "center";
        element.style.gap = "2px";
        element.innerHTML = `${
          !field.value
            ? `<div style="display: flex; align-items: center; justify-content: center; height: 16px; width: 20px;">${createSvgIcon(
                "Date"
              )}</div>
           <div style="font-size: 10px; line-height: 12px; text-align: center;">Date</div>`
            : ""
        }
        ${
          field.value
            ? `<div style="font-size: 12px; line-height: 14px; text-align: center; max-height: 60px; overflow: hidden; text-overflow: ellipsis;">${field.value}</div>`
            : ""
        }`;
        applyCommonStyling(element);

        // Add event listener for double click to edit
        element.addEventListener("dblclick", (e) => {
          e.stopPropagation();
          showTextFieldEditor(field, element);
        });
        break;

      case "Checkbox":
        const container = document.createElement("div");
        container.style.position = "relative";
        container.style.display = "flex";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        applyCommonStyling(container);

        // Create checkbox group label if field has a group property
        if (field.group) {
          const groupLabel = document.createElement("div");
          groupLabel.style.position = "absolute";
          groupLabel.style.top = "-18px";
          groupLabel.style.left = "0";
          groupLabel.style.fontSize = "10px";
          groupLabel.style.color = "#2868d7";
          groupLabel.textContent = field.group;
          container.appendChild(groupLabel);
        }

        // Create the checkbox input element
        const checkboxInput = document.createElement("input");
        checkboxInput.type = "checkbox";
        checkboxInput.style.transform = "scale(1.5)";
        checkboxInput.style.margin = "0";
        checkboxInput.checked = field.value === true;

        // Add click event to toggle checkbox state
        checkboxInput.addEventListener("click", (e) => {
          e.stopPropagation();

          // Update the field value in state
          setDroppedFields((prev) => {
            return prev.map((item) => {
              if (item.id === field.id) {
                return {
                  ...item,
                  value: checkboxInput.checked,
                };
              }
              return item;
            });
          });
        });

        container.appendChild(checkboxInput);

        // Create resize handle with transparent background
        const resizeHandle = document.createElement("div");
        resizeHandle.style.position = "absolute";
        resizeHandle.style.width = "10px";
        resizeHandle.style.height = "10px";
        resizeHandle.style.bottom = "0";
        resizeHandle.style.right = "0";
        resizeHandle.style.cursor = "nwse-resize";
        resizeHandle.style.backgroundColor = "transparent";
        resizeHandle.style.border = "1px solid rgba(40, 104, 215, 0.5)";
        resizeHandle.style.borderRadius = "0 0 4px 0";

        let isResizing = false;
        let originalWidth, originalHeight, originalX, originalY;

        resizeHandle.addEventListener("mousedown", (e) => {
          isResizing = true;
          originalWidth = parseInt(container.style.width);
          originalHeight = parseInt(container.style.height);
          originalX = e.clientX;
          originalY = e.clientY;
          e.preventDefault();
          e.stopPropagation();
        });

        document.addEventListener("mousemove", (e) => {
          if (!isResizing) return;

          const deltaX = e.clientX - originalX;
          const deltaY = e.clientY - originalY;

          // Calculate the maximum delta to maintain square aspect ratio
          const maxDelta = Math.max(deltaX, deltaY);

          // Ensure minimum size and maintain square aspect ratio
          const newSize = Math.max(30, originalWidth + maxDelta);

          container.style.width = `${newSize}px`;
          container.style.height = `${newSize}px`;

          // Scale the checkbox proportionally
          const scale = newSize / 30;
          checkboxInput.style.transform = `scale(${scale})`;

          e.preventDefault();
        });

        document.addEventListener("mouseup", () => {
          if (isResizing) {
            isResizing = false;

            setDroppedFields((prev) => {
              return prev.map((item) => {
                if (item.id === field.id) {
                  return {
                    ...item,
                    width: container.style.width,
                    height: container.style.height,
                  };
                }
                return item;
              });
            });
          }
        });

        container.appendChild(resizeHandle);
        element = container;
        break;

      default:
        element = document.createElement("div");
        element.textContent = `${field.tabType} - ${field.user.first_name} ${field.user.last_name}`;
        break;
    }

    // Set final element properties
    element.id = field.id;
    element.dataset.fieldType = field.tabType;
    element.dataset.userId = field.user.id;

    element.style.position = "absolute";
    element.style.left = `${field.xPos}%`;
    element.style.bottom = `${field.yPos}%`;

    // Set dimensions based on field type
    if (field.tabType === "Signature") {
      element.style.width = "160px";
      element.style.height = "36px";
    } else if (field.tabType === "Initial") {
      element.style.width = "60px";
      element.style.height = "36px";
    } else if (field.tabType === "TextBox") {
      element.style.width = "160px";
      element.style.height = "36px";
      element.style.backgroundColor = "rgba(0,0,0,.05)";
    } else if (field.tabType === "Date") {
      element.style.width = "160px";
      element.style.height = "36px";
      element.style.backgroundColor = "rgba(0,0,0,.05)";
    } else if (field.tabType === "Checkbox") {
      if (field.width && field.height) {
        element.style.width = field.width;
        element.style.height = field.height;
      } else {
        element.style.width = "30px";
        element.style.height = "30px";
      }
    } else {
      element.style.width = "100px";
      element.style.height = "30px";
      element.style.backgroundColor = "rgba(0,0,0,.05)";
    }

    // Add hover effects
    if (field.tabType !== "Checkbox") {
      element.style.backgroundColor = "rgba(0,0,0,.05)";

      element.addEventListener("mouseenter", () => {
        element.style.backgroundColor = "#cadbee";
      });

      element.addEventListener("mouseleave", () => {
        element.style.backgroundColor = "#e6eaff";
      });
    }

    // Make element draggable
    element.draggable = true;
    element.addEventListener("dragstart", (e) => {
      e.dataTransfer?.setData("text/plain", field.id);

      const ghostElement = element.cloneNode(true);
      ghostElement.style.opacity = "0.5";
      ghostElement.style.position = "absolute";
      ghostElement.style.top = "-1000px";
      document.body.appendChild(ghostElement);

      e.dataTransfer.setDragImage(ghostElement, 0, 0);

      setTimeout(() => {
        document.body.removeChild(ghostElement);
      }, 0);
    });

    element.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      element.draggable = true;

      // Check if the element is already selected to prevent double handling
      const alreadySelected = droppedFields.find(
        (item) => item.id === field.id
      )?.isSelected;
      if (!alreadySelected) {
        handleElementSelect(field.id);
      }

      if (field.tabType === "TextBox" && e.detail === 2) {
        // Only show text editor on double-click
        showTextFieldEditor(field, element);
      }
    });

    element.addEventListener("dragend", (e) => {
      setDroppedFields((prev) => {
        return prev.map((item) => {
          if (item.id === field.id) {
            return {
              ...item,
              xPos: parseFloat(element.style.left),
              yPos: parseFloat(element.style.bottom),
            };
          }
          return item;
        });
      });

      const floatingElement = document.getElementById("floating-drag-element");
      if (floatingElement) {
        document.body.removeChild(floatingElement);
      }
    });

    fieldWrapper.appendChild(element);
  };

  // Effect to render dropped fields
  useEffect(() => {
    droppedFields.forEach((field) => {
      const existingElement = document.getElementById(field.id);
      if (!existingElement) {
        addDroppedField(field, field.pageIndex);
      }
    });
  }, [droppedFields]);

  // Effect to update element styling based on selection state
  useEffect(() => {
    droppedFields.forEach((field) => {
      const element = document.getElementById(field.id);
      if (element) {
        element.style.border = `${field.borderWidth || 1}px ${
          field.borderStyle || "solid"
        } ${field.borderColor}`;

        if (field.isSelected) {
          element.style.boxShadow = "0 0 0 2px rgba(40, 104, 215, 0.5)";
        } else {
          element.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
        }
      }
    });
  }, [droppedFields]);

  // Handle document clicks for deselecting elements
  useEffect(() => {
    const handleDocumentClick = (e) => {
      let isCanvasClick = false;

      for (let i = 0; i < 10; i++) {
        // Assuming max 10 pages
        const canvas = document.getElementById(`myCanvas${i}`);
        const fieldWrap = document.getElementById(`fieldWrap${i}`);

        if (canvas && canvas.contains(e.target) && e.target === canvas) {
          isCanvasClick = true;
          break;
        }

        if (
          fieldWrap &&
          fieldWrap.contains(e.target) &&
          e.target === fieldWrap
        ) {
          isCanvasClick = true;
          break;
        }
      }

      if (isCanvasClick) {
        setDroppedFields((prev) => {
          return prev.map((item) => ({
            ...item,
            isSelected: false,
            borderColor:
              item.borderColor === "#2868d7" ? "#ff9999" : item.borderColor,
          }));
        });
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  return null; // This component doesn't render anything visible
};

export default FieldManager;
